import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Stack, Chip, CircularProgress, Alert,
    Table, TableContainer, TableHead, TableBody, TableRow, TableCell, TablePagination, TextField, Select, MenuItem,
    InputAdornment, useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import livechatApi from "./livechatApi";
import { PASTEL } from "./livechatTheme";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;
const ALL_ROLES = "";

const headSx = { fontWeight: 700, whiteSpace: "nowrap" };

/**
 * Whitelist autoleads — "Tambah Sales" di Kelola Sales Live Chat: admin mencari user Lotus (tabel
 * MySQL users) berdasarkan nama/email dan/atau role, lalu memasukkannya ke whitelist. Tanpa
 * pengecualian role: admin bebas menentukan siapa yang ikut autoleads. User yang sudah terdaftar
 * ditandai "Terdaftar". Backend menolak non-admin (403).
 */
export default function LeadSalesAddDialog({ open, onClose, onAdded }) {
    const isMobile = useMediaQuery("(max-width:600px)");
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [role, setRole] = useState(ALL_ROLES);
    const [roles, setRoles] = useState([]);
    const [page, setPage] = useState(0);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingUserId, setSavingUserId] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        livechatApi
            .listUserRoles()
            .then(({ data }) => {
                if (!cancelled) setRoles(data.data || []);
            })
            .catch(() => {
                if (!cancelled) setRoles([]);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    // Kata kunci / role berubah -> kembali ke halaman pertama (di-set bersamaan supaya 1 fetch saja).
    useEffect(() => {
        const next = keyword.trim();
        if (next === debouncedKeyword) return undefined;
        const timer = setTimeout(() => {
            setDebouncedKeyword(next);
            setPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [keyword, debouncedKeyword]);

    const handleRoleChange = (value) => {
        setRole(value);
        setPage(0);
    };

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        livechatApi
            .searchAutoAssignCandidates({
                keyword: debouncedKeyword || undefined,
                role: role || undefined,
                page: page + 1,
                limit: PAGE_SIZE,
            })
            .then(({ data }) => {
                if (cancelled) return;
                setUsers(data.data || []);
                setTotal(data.additional_data?.total || 0);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.message || "Gagal mencari user");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, debouncedKeyword, role, page]);

    const handleAdd = async (user) => {
        setSavingUserId(user.id);
        setError(null);
        try {
            const { data } = await livechatApi.setAgentMembership(user.id, true);
            setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, member: true } : u)));
            if (onAdded) onAdded(data.data || { userId: user.id, member: true });
        } catch (err) {
            console.error("Error in LeadSalesAddDialog.handleAdd:", err);
            setError(err?.response?.data?.message || "Gagal menambahkan sales");
        } finally {
            setSavingUserId(null);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
            <DialogTitle sx={{ pr: 6 }}>
                Tambah Sales Autoleads
                <IconButton onClick={onClose} aria-label="Tutup" sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Hanya user yang ditambahkan di sini yang ikut pembagian lead otomatis, bisa menyalakan
                    &quot;Autoleads&quot;, dan mengambil percakapan Unassigned. User baru ikut antrian
                    setelah membuka Live Chat dan menyalakan toggle-nya sendiri.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Cari nama atau email"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        inputProps={{ "aria-label": "Cari nama atau email" }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Select
                        size="small"
                        value={role}
                        onChange={(e) => handleRoleChange(e.target.value)}
                        displayEmpty
                        sx={{ minWidth: { sm: 220 } }}
                        inputProps={{ "aria-label": "Filter role" }}
                    >
                        <MenuItem value={ALL_ROLES}>Semua role</MenuItem>
                        {roles.map((r) => (
                            <MenuItem key={r.slug} value={r.slug}>
                                {r.name} ({r.userCount})
                            </MenuItem>
                        ))}
                    </Select>
                </Stack>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={28} sx={{ color: PASTEL.primary }} />
                    </Stack>
                ) : users.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        Tidak ada user yang cocok.
                    </Typography>
                ) : (
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headSx}>Nama</TableCell>
                                    <TableCell sx={headSx}>Email</TableCell>
                                    <TableCell sx={headSx}>Role</TableCell>
                                    <TableCell sx={headSx} align="right">Aksi</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{user.name || `User #${user.id}`}</TableCell>
                                        <TableCell sx={{ wordBreak: "break-all" }}>{user.email}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
                                                {(user.roles || []).map((r) => (
                                                    <Chip key={r.slug} size="small" label={r.name} sx={{ height: 20, fontSize: 11 }} />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            {user.member ? (
                                                <Chip size="small" label="Terdaftar" sx={{ backgroundColor: "rgba(46,125,50,0.1)", color: "#2e7d32" }} />
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleAdd(user)}
                                                    disabled={savingUserId === user.id}
                                                    sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                                                    aria-label={`Tambah ${user.name || user.id}`}
                                                >
                                                    Tambah
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {total > PAGE_SIZE && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_e, next) => setPage(next)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} dari ${count}`}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: "none" }}>Selesai</Button>
            </DialogActions>
        </Dialog>
    );
}
